import React, { useState } from 'react';
import { createAgent } from '../services/api';
import { AgentFormData, Agent, TeamMember, ContractAddress } from '../types/index';
import axios from 'axios';
import { Plus, X } from 'lucide-react';
import SuccessDialog from './SuccessDialog';

interface AddAgentModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: Agent) => void;
}

const AVAILABLE_CHAINS = [
    'Ethereum',
    'Solana',
    'Base',
    'Arbitrum',
    'Polygon',
    'Optimism',
    'Mode',
    'HyperEVM'
];
const CONTACT_METHODS = ['email', 'telegram', 'twitter'];

const AddAgentModal: React.FC<AddAgentModalProps> = ({ isOpen, onClose, onSubmit }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [contractAddresses, setContractAddresses] = useState<ContractAddress[]>([{ chain: '', address: '' }]);
    const [teamMembers, setTeamMembers] = useState<TeamMember[]>([{ name: '', role: '', twitter: '', linkedin: '' }]);
    const [contactMethod, setContactMethod] = useState('email');
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});
    const [showSuccess, setShowSuccess] = useState(false);

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        
        const formData = new FormData(e.target as HTMLFormElement);
        const errors: Record<string, string> = {};
        
        // Validate all URLs
        const urlFields = [
            { name: 'website_url', label: 'Website' },
            { name: 'github_url', label: 'GitHub' },
            { name: 'documentation_url', label: 'Documentation' },
            { name: 'x_url', label: 'X (Twitter)' },
            { name: 'discord_url', label: 'Discord' },
            { name: 'telegram_url', label: 'Telegram' },
            { name: 'cookie_url', label: 'Cookie' }
        ];

        urlFields.forEach(field => {
            const url = formData.get(field.name) as string;
            if (url) {
                const urlError = validateUrl(url, field.label);
                if (urlError) {
                    errors[field.name] = urlError;
                }
            }
        });

        // If there are validation errors, show them and stop submission
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            // Scroll to first error
            const firstErrorField = document.querySelector('[class*="border-red-500"]');
            firstErrorField?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }

        setIsSubmitting(true);

        try {
            const data: AgentFormData = {
                name: formData.get('name') as string,
                description: formData.get('description') as string,
                level_of_autonomy: formData.get('level_of_autonomy') as 'LOW' | 'MEDIUM' | 'HIGH' | 'FULL',
                market_cap: formData.get('market_cap') as string,
                token_utility: formData.get('token_utility') as string,
                open_source_status: formData.get('open_source_status') as 'CLOSED_SOURCE' | 'PARTIALLY_OPEN' | 'FULLY_OPEN' | 'UNKNOWN',
                frameworks: formData.get('frameworks')?.toString().split(',').map(f => f.trim()).filter(Boolean) || [],
                tags: formData.get('tags')?.toString().split(',').map(tag => tag.trim()).filter(Boolean) || [],
                chains: formData.get('chains')?.toString().split(',').map(chain => chain.trim()).filter(Boolean) || [],
                website_url: formData.get('website_url') as string || undefined,
                github_url: formData.get('github_url') as string || undefined,
                documentation_url: formData.get('documentation_url') as string || undefined,
                x_url: formData.get('x_url') as string || undefined,
                discord_url: formData.get('discord_url') as string || undefined,
                telegram_url: formData.get('telegram_url') as string || undefined,
                agent_symbol: formData.get('agent_symbol') as string || undefined,
                contract_addresses: contractAddresses.filter(addr => addr.chain && addr.address),
                total_supply: formData.get('total_supply') as string || undefined,
                team_anonymous: formData.get('team_anonymous') === 'on',
                team_members_social_links: teamMembers.filter(member => member.name || member.role),
                is_creator: formData.get('is_creator') === 'on',
                preferred_contact_method: {
                    email: formData.get('contact_email') as string || undefined,
                    telegram_username: formData.get('contact_telegram') as string || undefined,
                    twitter_username: formData.get('contact_twitter') as string || undefined
                },
                agent_team_status: formData.get('agent_team_status') as 'DOXXED' | 'ANON' || undefined,
            };

            const response = await createAgent(data);
            onSubmit(response);
            setShowSuccess(true);
        } catch (err) {
            if (axios.isAxiosError(err)) {
                setError(err.response?.data?.error || err.message);
            } else {
                setError(err instanceof Error ? err.message : 'Failed to create agent');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const addContractAddress = () => {
        setContractAddresses([...contractAddresses, { chain: '', address: '' }]);
    };

    const removeContractAddress = (index: number) => {
        setContractAddresses(contractAddresses.filter((_, i) => i !== index));
    };

    const addTeamMember = () => {
        setTeamMembers([...teamMembers, { name: '', role: '', twitter: '', linkedin: '' }]);
    };

    // Add validation functions
    const validateTokenSupply = (value: string): string => {
        if (value && !/^\d+$/.test(value)) {
            return "Please enter numbers only";
        }
        return "";
    };

    const validateUrl = (url: string, fieldName: string): string => {
        if (!url) return ""; // Allow empty URLs
        
        // Check if URL starts with https://
        if (!url.startsWith('https://')) {
            return `URL must start with https://`;
        }
        
        try {
            const urlObj = new URL(url);
            
            // Platform-specific validation
            switch (fieldName.toLowerCase()) {
                case 'github':
                    if (!urlObj.hostname.includes('github.com')) {
                        return 'Please enter a valid GitHub URL (https://github.com)';
                    }
                    break;
                case 'x (twitter)':
                    if (!urlObj.hostname.includes('x.com') && !urlObj.hostname.includes('twitter.com')) {
                        return 'Please enter a valid X/Twitter URL (https://x.com or https://twitter.com)';
                    }
                    break;
                case 'discord':
                    if (!urlObj.hostname.includes('discord.com') && !urlObj.hostname.includes('discord.gg')) {
                        return 'Please enter a valid Discord URL (https://discord.com or https://discord.gg)';
                    }
                    break;
                case 'telegram':
                    if (!urlObj.hostname.includes('t.me') && !urlObj.hostname.includes('telegram.me')) {
                        return 'Please enter a valid Telegram URL (https://t.me)';
                    }
                    break;
                case 'cookie':
                    if (!urlObj.hostname.includes('cookie.fun')) {
                        return 'Please enter a valid Cookie URL (https://cookie.fun)';
                    }
                    break;
                case 'documentation':
                case 'website':
                    // General URL validation for documentation and website
                    if (!urlObj.hostname) {
                        return 'Please enter a valid URL';
                    }
                    break;
            }
            
            return "";
        } catch {
            return `Please enter a valid URL starting with https://`;
        }
    };

    const handleSuccessClose = () => {
        setShowSuccess(false);
        onClose();
    };

    return (
        <>
            <div 
                className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-start justify-center z-50 overflow-y-auto"
                onClick={handleBackdropClick}
            >
                <div className="relative min-h-screen py-6 w-full flex items-start justify-center">
                    <div className="bg-[#0f0d15] rounded-xl w-full max-w-3xl mx-4 p-6 border border-gray-800 my-10">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-semibold text-white font-['Red_Hat_Display']">Add New Agent</h2>
                            <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {error && (
                            <div className="mb-4 p-4 bg-red-500/10 border border-red-500/50 rounded-lg text-red-500">
                                {error}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <h3 className="text-lg font-medium text-white pb-2 border-b border-gray-800">Basic Agent Information</h3>
                            <div>
                                <label className="block text-gray-400 mb-2 font-['Inter']">Name *</label>
                                <input
                                    type="text"
                                    name="name"
                                    required
                                    className="w-full px-4 py-2 rounded-lg bg-[#0D0B14] border border-gray-700 text-white focus:outline-none focus:border-purple-500 font-['Inter']"
                                />
                            </div>

                            {/* Agent Symbol */}
                            <div>
                                <label className="block text-gray-400 mb-2">Agent Symbol</label>
                                <input
                                    type="text"
                                    name="agent_symbol"
                                    className="w-full px-4 py-2 rounded-lg bg-[#0D0B14] border border-gray-700 text-white"
                                    placeholder="e.g., AITB"
                                />
                            </div>


                            <div>
                                <label className="block text-gray-400 mb-2 font-['Inter']">Description *</label>
                                <textarea
                                    name="description"
                                    rows={2}
                                    required
                                    className="w-full px-4 py-2 rounded-lg bg-[#0D0B14] border border-gray-700 text-white focus:outline-none focus:border-purple-500 font-['Inter']"
                                    placeholder="Enter description..."
                                />
                            </div>

                            {/* Contract Addresses */}
                            <div className="space-y-4">
                                <h3 className="text-lg font-medium text-white pb-2 border-b border-gray-800">
                                    Agent Details
                                </h3>

                                <label className="block text-gray-400 mb-2 font-['Inter']">Contract Addresses *</label>

                                {contractAddresses.map((address, index) => (
                                    <div key={index} className="flex gap-4">
                                        <div className="w-1/3 relative">
                                            <select
                                                value={address.chain}
                                                onChange={(e) => {
                                                    const newAddresses = [...contractAddresses];
                                                    newAddresses[index].chain = e.target.value;
                                                    setContractAddresses(newAddresses);
                                                }}
                                                className="w-full px-4 py-2 rounded-lg bg-[#0D0B14] border border-gray-700 text-white focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 appearance-none"
                                            >
                                                <option value="">Select Chain</option>
                                                {AVAILABLE_CHAINS.map(chain => (
                                                    <option key={chain} value={chain}>{chain}</option>
                                                ))}
                                            </select>
                                            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                </svg>
                                            </div>
                                        </div>
                                        <input
                                            type="text"
                                            value={address.address}
                                            onChange={(e) => {
                                                const newAddresses = [...contractAddresses];
                                                newAddresses[index].address = e.target.value;
                                                setContractAddresses(newAddresses);
                                            }}
                                            placeholder="Contract Address"
                                            className="flex-1 px-4 py-2 rounded-lg bg-[#0D0B14] border border-gray-700 text-white focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                                        />
                                        {index > 0 && (
                                            <button
                                                type="button"
                                                onClick={() => removeContractAddress(index)}
                                                className="p-2 text-gray-400 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors"
                                            >
                                                <X className="w-5 h-5" />
                                            </button>
                                        )}
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    onClick={addContractAddress}
                                    className="flex items-center gap-2 text-purple-500 hover:text-purple-400 transition-colors mt-2"
                                >
                                    <Plus className="w-4 h-4" /> Add Contract Address
                                </button>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-gray-400 mb-2 font-['Inter']">Level of Autonomy *</label>
                                    <div className="relative">
                                    <select
                                        name="level_of_autonomy"
                                        required
                                            className="w-full px-4 py-2 pr-8 rounded-lg bg-[#0D0B14] border border-gray-700 text-white focus:outline-none focus:border-purple-500 font-['Inter'] appearance-none"
                                    >
                                        <option value="LOW">Low</option>
                                        <option value="MEDIUM">Medium</option>
                                        <option value="HIGH">High</option>
                                        <option value="FULL">Full</option>
                                    </select>
                                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-gray-400 mb-2 font-['Inter']">Open Source Status *</label>
                                    <div className="relative">
                                    <select
                                        name="open_source_status"
                                        required
                                            className="w-full px-4 py-2 pr-8 rounded-lg bg-[#0D0B14] border border-gray-700 text-white focus:outline-none focus:border-purple-500 font-['Inter'] appearance-none"
                                    >
                                        <option value="UNKNOWN">Unknown</option>
                                        <option value="CLOSED_SOURCE">Closed Source</option>
                                            {/* <option value="PARTIALLY_OPEN">Partially Open</option> */}
                                        <option value="FULLY_OPEN">Fully Open</option>
                                    </select>
                                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </div>
                                </div>
                            </div>

                                <div>
                                    <label className="block text-gray-400 mb-2">Token Utility Level *</label>
                                    <div className="relative">
                                    <select
                                        name="token_utility"
                                        required
                                            className="w-full px-4 py-2 pr-8 rounded-lg bg-[#0D0B14] border border-gray-700 text-white focus:outline-none focus:border-purple-500 appearance-none"
                                    >
                                        <option value="">Select Level</option>
                                        <option value="1">One</option>
                                        <option value="2">Two</option>
                                        <option value="3">Three</option>
                                        <option value="4">Four</option>
                                        <option value="5">Five</option>
                                    </select>
                                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            </div>






                            {/* Add Token Supply field */}
                            <div>
                                <label className="block text-gray-400 mb-2 text-sm">Token Supply</label>
                                <input
                                    type="text"
                                    name="total_supply"
                                    placeholder="Enter total token supply"
                                    onChange={(e) => {
                                        const error = validateTokenSupply(e.target.value);
                                        setFormErrors(prev => ({
                                            ...prev,
                                            total_supply: error
                                        }));
                                    }}
                                    className={`w-full px-4 py-2 rounded-lg bg-[#0D0B14] border ${
                                        formErrors.total_supply ? 'border-red-500' : 'border-gray-700'
                                    } text-white focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500`}
                                />
                                {formErrors.total_supply && (
                                    <p className="text-red-500 text-sm mt-1">{formErrors.total_supply}</p>
                                )}
                            </div>


                            {/* Arrays */}
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-gray-400 mb-2 font-['Inter']">Frameworks</label>
                                    <input
                                        type="text"
                                        name="frameworks"
                                        placeholder="e.g., TensorFlow, PyTorch (comma-separated)"
                                        className="w-full px-4 py-2 rounded-lg bg-[#0D0B14] border border-gray-700 text-white focus:outline-none focus:border-purple-500 font-['Inter']"
                                    />
                                </div>

                                <div>
                                    <label className="block text-gray-400 mb-2 font-['Inter']">Tags</label>
                                    <input
                                        type="text"
                                        name="tags"
                                        placeholder="e.g., trading, defi (comma-separated)"
                                        className="w-full px-4 py-2 rounded-lg bg-[#0D0B14] border border-gray-700 text-white focus:outline-none focus:border-purple-500 font-['Inter']"
                                    />
                                </div>
                                {/* 
                                <div>
                                    <label className="block text-gray-400 mb-2 text-sm">Chains *</label>
                                    <div className="flex flex-wrap gap-2 p-3 rounded-lg bg-[#0D0B14] border border-gray-700">
                                        {AVAILABLE_CHAINS.map(chain => (
                                            <label key={chain} className="flex items-center gap-2">
                                    <input
                                                    type="checkbox"
                                        name="chains"
                                                    value={chain}
                                                    className="form-checkbox bg-[#0D0B14] border-gray-700 rounded text-purple-500 focus:ring-purple-500"
                                    />
                                                <span className="text-gray-300 text-sm">{chain}</span>
                                            </label>
                                        ))}
                                </div>
                                </div> */}
                            </div>

                            {/* URLs */}
                            <div className="space-y-4">
                                <h3 className="text-lg font-medium text-white pb-2 border-b border-gray-800">Links</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-gray-400 mb-2 font-['Inter']">Website URL</label>
                                        <input
                                            type="url"
                                            name="website_url"
                                            placeholder="https://..."
                                            onChange={(e) => {
                                                const error = validateUrl(e.target.value, 'website');
                                                setFormErrors(prev => ({
                                                    ...prev,
                                                    website_url: error
                                                }));
                                            }}
                                            onBlur={(e) => {
                                                const error = validateUrl(e.target.value, 'website');
                                                setFormErrors(prev => ({
                                                    ...prev,
                                                    website_url: error
                                                }));
                                            }}
                                            className={`w-full px-4 py-2 rounded-lg bg-[#0D0B14] border ${
                                                formErrors.website_url ? 'border-red-500' : 'border-gray-700'
                                            } text-white focus:outline-none focus:border-purple-500 font-['Inter']`}
                                        />
                                        {formErrors.website_url && (
                                            <p className="text-red-500 text-sm mt-1">{formErrors.website_url}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-gray-400 mb-2 font-['Inter']">GitHub URL</label>
                                        <input
                                            type="url"
                                            name="github_url"
                                            placeholder="https://github.com/..."
                                            onChange={(e) => {
                                                const error = validateUrl(e.target.value, 'GitHub');
                                                setFormErrors(prev => ({
                                                    ...prev,
                                                    github_url: error
                                                }));
                                            }}
                                            onBlur={(e) => {
                                                const error = validateUrl(e.target.value, 'GitHub');
                                                setFormErrors(prev => ({
                                                    ...prev,
                                                    github_url: error
                                                }));
                                            }}
                                            className={`w-full px-4 py-2 rounded-lg bg-[#0D0B14] border ${
                                                formErrors.github_url ? 'border-red-500' : 'border-gray-700'
                                            } text-white focus:outline-none focus:border-purple-500 font-['Inter']`}
                                        />
                                        {formErrors.github_url && (
                                            <p className="text-red-500 text-sm mt-1">{formErrors.github_url}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-gray-400 mb-2 font-['Inter']">Documentation URL</label>
                                        <input
                                            type="url"
                                            name="documentation_url"
                                            placeholder="https://docs..."
                                            onChange={(e) => {
                                                const error = validateUrl(e.target.value, 'Documentation');
                                                setFormErrors(prev => ({
                                                    ...prev,
                                                    documentation_url: error
                                                }));
                                            }}
                                            onBlur={(e) => {
                                                const error = validateUrl(e.target.value, 'Documentation');
                                                setFormErrors(prev => ({
                                                    ...prev,
                                                    documentation_url: error
                                                }));
                                            }}
                                            className={`w-full px-4 py-2 rounded-lg bg-[#0D0B14] border ${
                                                formErrors.documentation_url ? 'border-red-500' : 'border-gray-700'
                                            } text-white focus:outline-none focus:border-purple-500 font-['Inter']`}
                                        />
                                        {formErrors.documentation_url && (
                                            <p className="text-red-500 text-sm mt-1">{formErrors.documentation_url}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-gray-400 mb-2 font-['Inter']">X (Twitter) URL</label>
                                        <input
                                            type="url"
                                            name="x_url"
                                            placeholder="https://x.com/... or https://twitter.com/..."
                                            onChange={(e) => {
                                                const error = validateUrl(e.target.value, 'X (Twitter)');
                                                setFormErrors(prev => ({
                                                    ...prev,
                                                    x_url: error
                                                }));
                                            }}
                                            onBlur={(e) => {
                                                const error = validateUrl(e.target.value, 'X (Twitter)');
                                                setFormErrors(prev => ({
                                                    ...prev,
                                                    x_url: error
                                                }));
                                            }}
                                            className={`w-full px-4 py-2 rounded-lg bg-[#0D0B14] border ${
                                                formErrors.x_url ? 'border-red-500' : 'border-gray-700'
                                            } text-white focus:outline-none focus:border-purple-500 font-['Inter']`}
                                        />
                                        {formErrors.x_url && (
                                            <p className="text-red-500 text-sm mt-1">{formErrors.x_url}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-gray-400 mb-2 font-['Inter']">Discord URL</label>
                                        <input
                                            type="url"
                                            name="discord_url"
                                            placeholder="https://discord.gg/..."
                                            onChange={(e) => {
                                                const error = validateUrl(e.target.value, 'Discord');
                                                setFormErrors(prev => ({
                                                    ...prev,
                                                    discord_url: error
                                                }));
                                            }}
                                            onBlur={(e) => {
                                                const error = validateUrl(e.target.value, 'Discord');
                                                setFormErrors(prev => ({
                                                    ...prev,
                                                    discord_url: error
                                                }));
                                            }}
                                            className={`w-full px-4 py-2 rounded-lg bg-[#0D0B14] border ${
                                                formErrors.discord_url ? 'border-red-500' : 'border-gray-700'
                                            } text-white focus:outline-none focus:border-purple-500 font-['Inter']`}
                                        />
                                        {formErrors.discord_url && (
                                            <p className="text-red-500 text-sm mt-1">{formErrors.discord_url}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-gray-400 mb-2 font-['Inter']">Telegram URL</label>
                                        <input
                                            type="url"
                                            name="telegram_url"
                                            placeholder="https://t.me/..."
                                            onChange={(e) => {
                                                const error = validateUrl(e.target.value, 'Telegram');
                                                setFormErrors(prev => ({
                                                    ...prev,
                                                    telegram_url: error
                                                }));
                                            }}
                                            onBlur={(e) => {
                                                const error = validateUrl(e.target.value, 'Telegram');
                                                setFormErrors(prev => ({
                                                    ...prev,
                                                    telegram_url: error
                                                }));
                                            }}
                                            className={`w-full px-4 py-2 rounded-lg bg-[#0D0B14] border ${
                                                formErrors.telegram_url ? 'border-red-500' : 'border-gray-700'
                                            } text-white focus:outline-none focus:border-purple-500 font-['Inter']`}
                                        />
                                        {formErrors.telegram_url && (
                                            <p className="text-red-500 text-sm mt-1">{formErrors.telegram_url}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-gray-400 mb-2 font-['Inter']">Cookie URL</label>
                                        <input
                                            type="url"
                                            name="cookie_url"
                                            placeholder="https://cookie.fun/..."
                                            onChange={(e) => {
                                                const error = validateUrl(e.target.value, 'Cookie');
                                                setFormErrors(prev => ({
                                                    ...prev,
                                                    cookie_url: error
                                                }));
                                            }}
                                            onBlur={(e) => {
                                                const error = validateUrl(e.target.value, 'Cookie');
                                                setFormErrors(prev => ({
                                                    ...prev,
                                                    cookie_url: error
                                                }));
                                            }}
                                            className={`w-full px-4 py-2 rounded-lg bg-[#0D0B14] border ${
                                                formErrors.cookie_url ? 'border-red-500' : 'border-gray-700'
                                            } text-white focus:outline-none focus:border-purple-500 font-['Inter']`}
                                        />
                                        {formErrors.cookie_url && (
                                            <p className="text-red-500 text-sm mt-1">{formErrors.cookie_url}</p>
                                        )}
                                    </div>
                                </div>
                            </div>



                            {/* Team Members */}
                            <div className="space-y-4">
                                <h3 className="text-lg font-medium text-white pb-2 border-b border-gray-800 mb-4">
                                    Team Information
                                </h3>

                                {/* Creator Checkbox */}
                                <div>
                                    <label className="flex items-center gap-2 text-gray-400">
                                        <input
                                            type="checkbox"
                                            name="is_creator"
                                            className="appearance-none h-6 w-6 border border-purple-500 rounded bg-[#0D0B14] checked:bg-[#0D0B14] checked:border-purple-500 focus:outline-none cursor-pointer relative
                                            after:content-[''] after:absolute after:left-[3px] after:top-[4px] 
                                            after:w-[16px] after:h-[16px] after:opacity-0
                                            checked:after:opacity-100 checked:after:bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCI+PHBhdGggZD0iTTkgMTYuMTdMNC44MyAxMmwtMS40MiAxLjQxTDkgMTkgMjEgN2wtMS40MS0xLjQxeiIgZmlsbD0iI0E4NTVGNyIvPjwvc3ZnPg==')]"
                                        />
                                        Are you the creator?
                                    </label>
                                </div>

                                <div>
                                    <label className="block text-gray-400">Team Members Social link</label>
                                    {teamMembers.map((member, index) => (
                                        <div key={index} className="space-y-2 border-gray-700 rounded-lg">
                                            <label className="text-gray-400 text-sm mt-2 pt-2">Member {index + 1}</label>
                                            <div className="flex gap-2">
                                                <input
                                                    type="text"
                                                    value={member.name}
                                                    onChange={(e) => {
                                                        const newMembers = [...teamMembers];
                                                        newMembers[index].name = e.target.value;
                                                        setTeamMembers(newMembers);
                                                    }}
                                                    placeholder="Add Link here"
                                                    className="w-full px-4 py-2 rounded-lg bg-[#0D0B14] border border-gray-700 text-white"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const newMembers = teamMembers.filter((_, i) => i !== index);
                                                        setTeamMembers(newMembers);
                                                    }}
                                                    className="p-2 hover:text-red-400 text-gray-400"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                    <button
                                        type="button"
                                        onClick={addTeamMember}
                                        className="flex items-center gap-2 text-purple-500 hover:text-purple-400 mt-2"
                                    >
                                        <Plus className="w-4 h-4" /> Add Team Member
                                    </button>
                                </div>
                            </div>




                            {/* Team Status Toggle */}
                            <div className="space-y-4">

                                <div>
                                    <label className="block text-gray-400 mb-4">
                                        Is the team doxxed or anonymous?
                                    </label>
                                    <div className="flex gap-4">
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                                type="radio"
                                                name="agent_team_status"
                                                value="DOXXED"
                                                className="sr-only peer"
                                            />
                                            <div className="w-24 h-10 bg-[#0D0B14] border border-gray-700 peer-checked:border-purple-500 rounded-lg flex items-center justify-center text-sm text-gray-400 peer-checked:text-white peer-checked:bg-purple-500 transition-all">
                                                Doxxed
                                            </div>
                                        </label>
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                                type="radio"
                                                name="agent_team_status"
                                                value="ANON"
                                                className="sr-only peer"
                                            />
                                            <div className="w-24 h-10 bg-[#0D0B14] border border-gray-700 peer-checked:border-purple-500 rounded-lg flex items-center justify-center text-sm text-gray-400 peer-checked:text-white peer-checked:bg-purple-500 transition-all">
                                                Anonymous
                                            </div>
                                        </label>
                                    </div>
                                </div>
                            </div>



                            {/* Preferred Contact Method */}

                            <div className="space-y-2">
                                <label className="block text-gray-400">Preferred Contact Method</label>
                                <div className="flex gap-4 items-center">
                                    <div className="relative w-40">
                                        <select
                                            value={contactMethod}
                                            onChange={(e) => setContactMethod(e.target.value)}
                                            className="w-full px-4 py-2.5 rounded-lg bg-[#0D0B14] border border-gray-700 text-white appearance-none cursor-pointer focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                                        >
                                            {CONTACT_METHODS.map(method => (
                                                <option key={method} value={method}>{method}</option>
                                            ))}
                                        </select>
                                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
                                            <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                                                <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
                                            </svg>
                                        </div>
                                    </div>
                                    <input
                                        type="text"
                                        name={`contact_${contactMethod}`}
                                        placeholder={`Enter your ${contactMethod}`}
                                        className="flex-1 px-4 py-2.5 rounded-lg bg-[#0D0B14] border border-gray-700 text-white focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                                    />
                                </div>
                            </div>

                            <div className="flex justify-end gap-4 mt-8">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="px-6 py-2 rounded-lg border border-gray-700 text-gray-400 hover:text-white hover:border-gray-600 transition-colors"
                                    disabled={isSubmitting}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-6 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? 'Adding...' : 'Add Agent'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* Success Dialog */}
            <SuccessDialog
                isOpen={showSuccess}
                message="Your agent data is under review by the team, Thank you for your patience!"
                onClose={handleSuccessClose}
            />
        </>
    );
};

export default AddAgentModal;